import { createFileRoute, Outlet, redirect, retainSearchParams } from '@tanstack/react-router'
import { Alert, CircularProgress, Stack } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { UserQueries, UsersPage } from '@portal/app-admin'
import { z } from 'zod'

function UsersErrorComponent() {
  const { t } = useTranslation('admin-console')
  return <Alert severity="error">{t('users.table.error')}</Alert>
}

export const Route = createFileRoute('/_auth/preferences/users')({
  component: UsersLayout,
  beforeLoad: ({ context: { effectiveUser, scopedEnablements }, search }) => {
    if (!scopedEnablements.global().hasStandardEnablement('User', 'MANAGE')) {
      throw redirect({
        to: '/403',
        replace: true,
      })
    }

    const canManageAllOrgs =
      effectiveUser?.orgId === 1 &&
      scopedEnablements.global().hasStandardEnablement('Organization', 'ADMIN')

    if (search.orgId !== undefined && !canManageAllOrgs) {
      // A client-side redirect is re-built from its href, which drops the search function, so
      // retainSearchParams would restore orgId and the redirect would repeat. A document
      // reload starts from the clean URL.
      throw redirect({
        to: '.',
        search: (prev) => ({ ...prev, orgId: undefined }),
        replace: true,
        reloadDocument: true,
      })
    }

    return {
      canManageAllOrgs,
      effectiveUserOrgId: effectiveUser?.orgId,
    }
  },
  validateSearch: z.object({
    orgId: z.coerce.number().int().positive().optional(),
  }),
  search: {
    middlewares: [retainSearchParams(['orgId'])],
  },
  loaderDeps: ({ search: { orgId } }) => ({ orgId }),
  loader: ({ context: { queryClient }, deps: { orgId } }) =>
    Promise.all([
      queryClient.ensureQueryData(UserQueries.list(orgId)),
      queryClient.ensureQueryData(UserQueries.orgSettings(orgId)),
    ]),
  pendingComponent: () => (
    <Stack
      sx={{
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
      }}
    >
      <CircularProgress />
    </Stack>
  ),
  errorComponent: UsersErrorComponent,
})

function UsersLayout() {
  const { orgId } = Route.useSearch()
  const { canManageAllOrgs, effectiveUserOrgId } = Route.useRouteContext()
  const navigate = Route.useNavigate()

  return (
    <UsersPage
      orgId={orgId}
      effectiveUserOrgId={effectiveUserOrgId}
      canManageAllOrgs={canManageAllOrgs}
      onOrgChange={(nextOrgId) =>
        void navigate({
          search: (prev) => ({ ...prev, orgId: nextOrgId ?? undefined }),
          replace: true,
        })
      }
    >
      <Outlet />
    </UsersPage>
  )
}
